import { CartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import { MeFavoritesProductsQueryResult, MeQueryResult } from '@graphql/queries/__generated__/me.generated'
import { RewardProgramsQuery } from '@graphql/queries/__generated__/reward-programs.generated'
import React from 'react'
import { Link } from 'react-router-dom'
import { ACCOUNT_LOGIN_URL } from '../../urls'
import CartFavorites from './components/CartFavorites'
import CartLinesContainer from './components/CartLinesContainer'
import CartLinesTotal from './components/CartLinesTotal'
import { TCartParams, TCartSetParams } from './types'
import { useTranslation } from 'react-i18next'
import { CurrentCurrencyExchangeRateQueryResult } from '@graphql/queries/__generated__/shipping.generated'

interface PageProps {
  isAuthenticated: boolean
  lineLoading: number | null
  cartQueryResult: CartQueryResult
  favoritesQueryResult: MeFavoritesProductsQueryResult
  meQueryResult: MeQueryResult
  firstRewardProgram: RewardProgramsQuery['rewardPrograms']['edges'][0] | null
  params: TCartParams
  setParams: TCartSetParams
  currencyExchangeRateQueryResult: CurrentCurrencyExchangeRateQueryResult

  handleSignIn(): void

  setLineLoading(pk: number | null): void

  onClickProduct(specId: string | number, slug: string): void

  handleRemoveFromFavorites(productPk: number): Promise<void>
}

const Page: React.FC<PageProps> = ({
  handleSignIn,
  cartQueryResult,
  isAuthenticated,
  lineLoading,
  setLineLoading,
  onClickProduct,
  favoritesQueryResult,
  handleRemoveFromFavorites,
  params,
  setParams,
  meQueryResult,
  firstRewardProgram,
  currencyExchangeRateQueryResult,
}) => {
  const { t } = useTranslation()
  return (
    <div
      className='p-0 sm:p-24 flex flex-col md:flex-row justify-center mx-auto max-w-[1300px]'
      style={{ minHeight: 'inherit' }}
    >
      <div className='w-full'>
        <div className='flex items-center flex-wrap mb-16 px-12 sm:px-0 mt-20 md:mt-0'>
          <h1 className='font-bold text-xl leading-24 mr-12'>{t('title.shoppingBag', 'Shopping Bag')}</h1>
          {!isAuthenticated && (
            <span className='ml-auto leading-18 text-13'>
              <span className='text-gray-700 mr-5'>{t('haveAnAccount', 'Have an account?')}</span>
              <Link
                className='text-blue no-underline'
                to={ACCOUNT_LOGIN_URL}
                onClick={e => {
                  e.preventDefault()
                  handleSignIn()
                }}
              >
                {t('button.signIn', 'Sign in')}
              </Link>
            </span>
          )}
        </div>
        <div className='flex flex-col md:flex-row'>
          <div className='md:w-900 sm:mr-24'>
            <CartLinesContainer
              isAuthenticated={isAuthenticated}
              lineLoading={lineLoading}
              setLineLoading={setLineLoading}
              onClickProduct={onClickProduct}
            />
            {isAuthenticated && (
              <div className='mt-24'>
                <CartFavorites
                  favoritesQueryResult={favoritesQueryResult}
                  onClickProduct={onClickProduct}
                  favoritesLineLoading={lineLoading}
                  handleRemoveFromFavorites={handleRemoveFromFavorites}
                  page={params.favoritesPage || 1}
                  setPage={(page: number) => setParams({ favoritesPage: page }, 'replaceIn')}
                />
              </div>
            )}
          </div>

          <div className='w-full md:w-400 mt-20 md:mt-0 flex flex-col'>
            <CartLinesTotal
              isAuthenticated={isAuthenticated}
              cartQueryResult={cartQueryResult}
              handleSignIn={handleSignIn}
              loading={!!lineLoading || cartQueryResult.loading}
              meQueryResult={meQueryResult}
              firstRewardProgram={firstRewardProgram}
              currencyExchangeRateQueryResult={currencyExchangeRateQueryResult}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Page
